import { useRef, useState, type PointerEvent } from "react";
import type { UserProfile } from "@shared/schema";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import { queryClient } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export function RTSignatureSection({ user, canManage }: { user: UserProfile; canManage: boolean }) {
  const { session } = useAuth();
  const { toast } = useToast();
  const canvas = useRef<HTMLCanvasElement>(null);
  const pointer = useRef<number | null>(null);
  const [open, setOpen] = useState(false);
  const [hasInk, setHasInk] = useState(false);
  const [stamp, setStamp] = useState(true);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState<string>();
  const signatureUrl = preview ?? user.assinaturaUrl;

  function point(event: PointerEvent<HTMLCanvasElement>) {
    const rect = event.currentTarget.getBoundingClientRect();
    return { x: (event.clientX - rect.left) * 800 / rect.width, y: (event.clientY - rect.top) * 300 / rect.height };
  }

  function start(event: PointerEvent<HTMLCanvasElement>) {
    if (saving || pointer.current !== null || (event.pointerType === "mouse" && event.button !== 0)) return;
    pointer.current = event.pointerId;
    event.currentTarget.setPointerCapture(event.pointerId);
    const ctx = event.currentTarget.getContext("2d")!;
    const { x, y } = point(event);
    ctx.strokeStyle = "#172033";
    ctx.fillStyle = "#172033";
    ctx.lineWidth = 3;
    ctx.lineCap = "round";
    ctx.beginPath(); ctx.arc(x, y, 1.5, 0, Math.PI * 2); ctx.fill();
    ctx.beginPath(); ctx.moveTo(x, y);
    setHasInk(true);
  }

  function move(event: PointerEvent<HTMLCanvasElement>) {
    if (pointer.current !== event.pointerId) return;
    const ctx = event.currentTarget.getContext("2d")!;
    const { x, y } = point(event);
    ctx.lineTo(x, y); ctx.stroke();
  }

  async function save(base64: string, mimeType: string) {
    setSaving(true);
    try {
      const res = await fetch(`/api/usuarios/${user.id}/assinatura`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${session?.access_token}` },
        body: JSON.stringify({ base64, mimeType }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Não foi possível salvar a assinatura.");
      setPreview(data.assinaturaUrl);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["/api/usuarios"] }),
        queryClient.invalidateQueries({ queryKey: ["/api/auth/me"] }),
      ]);
      setOpen(false);
      toast({ title: "Assinatura cadastrada" });
    } catch (error) {
      toast({ title: "Erro ao salvar assinatura", description: error instanceof Error ? error.message : "Tente novamente.", variant: "destructive" });
    } finally { setSaving(false); }
  }

  function saveDrawing() {
    const image = document.createElement("canvas");
    image.width = 800; image.height = 300;
    const ctx = image.getContext("2d")!;
    ctx.fillStyle = "white"; ctx.fillRect(0, 0, 800, 300);
    if (stamp) {
      ctx.fillStyle = "#7b8798";
      ctx.textAlign = "center";
      ctx.font = "bold 28px sans-serif";
      ctx.fillText(user.nome, 400, 190, 750);
      ctx.font = "24px sans-serif";
      ctx.fillText("Engenheiro Mecânico", 400, 226);
      ctx.fillText(`CREA ${user.crea}`, 400, 262, 750);
    }
    ctx.drawImage(canvas.current!, 0, 0);
    void save(image.toDataURL("image/png").split(",")[1], "image/png");
  }

  async function upload(file?: File) {
    if (!file) return;
    if (!["image/png", "image/jpeg"].includes(file.type) || file.size === 0 || file.size > 5 * 1024 * 1024) {
      toast({ title: "Selecione uma imagem PNG ou JPG de até 5 MB.", variant: "destructive" }); return;
    }
    const reader = new FileReader();
    reader.onload = () => void save(String(reader.result).split(",")[1], file.type);
    reader.onerror = () => toast({ title: "Não foi possível ler a imagem.", variant: "destructive" });
    reader.readAsDataURL(file);
  }

  return <section className="mt-3 space-y-3 border-t pt-3" aria-label="Assinatura do RT">
    <p className="text-sm font-medium">Assinatura / rubrica</p>
    {signatureUrl ? <img src={signatureUrl} alt={`Assinatura de ${user.nome}`} className="h-28 max-w-full rounded border bg-white object-contain" /> : <p className="text-sm text-muted-foreground">Assinatura não cadastrada.</p>}
    {canManage && <div className="flex flex-wrap gap-2">
      <Button variant="outline" disabled={saving} onClick={() => { setHasInk(false); pointer.current = null; setOpen(true); }}>Desenhar assinatura</Button>
      <div className="space-y-1">
        <Label htmlFor={`signature-upload-${user.id}`}>Enviar imagem (PNG ou JPG, até 5 MB)</Label>
        <Input id={`signature-upload-${user.id}`} type="file" accept="image/png,image/jpeg" disabled={saving} onChange={e => { void upload(e.target.files?.[0]); e.target.value = ""; }} />
      </div>
      {saving && <p role="status" className="text-sm">Salvando assinatura…</p>}
    </div>}
    <Dialog open={open} onOpenChange={value => { if (!saving) setOpen(value); }}>
      <DialogContent className="max-h-[90dvh] overflow-y-auto sm:max-w-xl">
        <DialogHeader><DialogTitle>Desenhar assinatura / rubrica</DialogTitle><DialogDescription>Use o mouse, o dedo ou uma caneta. O carimbo será salvo junto à assinatura.</DialogDescription></DialogHeader>
        <div className="relative overflow-hidden rounded border bg-white">
          {stamp && <div aria-hidden="true" className="pointer-events-none absolute inset-x-2 bottom-3 text-center text-slate-500"><p className="truncate font-semibold">{user.nome}</p><p>Engenheiro Mecânico</p><p className="truncate">CREA {user.crea || "não cadastrado"}</p></div>}
          <canvas ref={canvas} width={800} height={300} aria-label="Área para desenhar assinatura" className="relative block aspect-[8/3] w-full touch-none" onPointerDown={start} onPointerMove={move} onPointerUp={() => { pointer.current = null; }} onPointerCancel={() => { pointer.current = null; }} />
        </div>
        <Label className="flex items-center gap-2"><input type="checkbox" checked={stamp} disabled={saving} onChange={e => setStamp(e.target.checked)} />Incluir carimbo com nome, profissão e CREA</Label>
        {stamp && !user.crea?.trim() && <p className="text-sm text-amber-800">Cadastre seu CREA no perfil para incluir o carimbo.</p>}
        <div className="flex flex-wrap justify-end gap-2">
          <Button variant="outline" disabled={saving} onClick={() => { canvas.current?.getContext("2d")?.clearRect(0, 0, 800, 300); setHasInk(false); }}>Limpar</Button>
          <Button disabled={saving || !hasInk || (stamp && !user.crea?.trim())} onClick={saveDrawing}>{saving ? "Salvando…" : "Salvar assinatura"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  </section>;
}
